import React, { useState } from "react";
import {
  ArrayField,
  Datagrid,
  TextField,
  NumberField,
  BooleanField,
  ChipField,
  Button,
  useRecordContext,
  useDataProvider,
  useNotify,
  useRefresh,
  SimpleForm,
  TextInput,
  NumberInput,
  SelectInput,
  BooleanInput,
  required,
  minValue,
} from "react-admin";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";

const limiterTypes = [
  { id: "messages_per_month", name: "Сообщения в месяц" },
  { id: "challenges_create_or_join", name: "Челленджи" },
  { id: "ai_requests_per_day", name: "AI запросы в день" },
  { id: "storage_gb", name: "Хранилище (GB)" },
  { id: "team_members", name: "Участники команды" },
];

const limiterPeriods = [
  { id: "daily", name: "День" },
  { id: "weekly", name: "Неделя" },
  { id: "monthly", name: "Месяц" },
  { id: "yearly", name: "Год" },
  { id: "total", name: "Общий" },
];

export const SubscriptionPlanLimiters = () => {
  const record = useRecordContext();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const refresh = useRefresh();
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newLimiter, setNewLimiter] = useState({
    subscriptionPlanId: record?.id,
    type: "",
    name: "",
    description: "",
    limitValue: 0,
    period: "monthly",
    isActive: true,
    isHardLimit: false,
  });

  const handleCreateLimiter = async () => {
    try {
      await dataProvider.create("subscriptionLimiters", {
        data: newLimiter,
      });
      notify("Лимитер успешно создан", { type: "success" });
      setShowCreateDialog(false);
      setNewLimiter({
        subscriptionPlanId: record?.id,
        type: "",
        name: "",
        description: "",
        limitValue: 0,
        period: "monthly",
        isActive: true,
        isHardLimit: false,
      });
      refresh();
    } catch (error) {
      notify("Ошибка при создании лимитера", { type: "error" });
    }
  };

  const handleDeleteLimiter = async (limiterId: string) => {
    if (window.confirm("Вы уверены, что хотите удалить этот лимитер?")) {
      try {
        await dataProvider.delete("subscriptionLimiters", { id: limiterId });
        notify("Лимитер успешно удален", { type: "success" });
        refresh();
      } catch (error) {
        notify("Ошибка при удалении лимитера", { type: "error" });
      }
    }
  };

  // Ensure limiters is always an array
  const limiters =
    record?.limiters && Array.isArray(record.limiters) ? record.limiters : [];

  if (!limiters || limiters.length === 0) {
    return (
      <div>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "16px",
          }}
        >
          <h3>Лимитеры плана</h3>
          <Button
            label="Добавить лимитер"
            onClick={() => setShowCreateDialog(true)}
            variant="contained"
            color="primary"
          />
        </div>
        <div>Нет лимитеров для этого плана</div>
      </div>
    );
  }

  return (
    <div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "16px",
        }}
      >
        <h3>Лимитеры плана</h3>
        <Button
          label="Добавить лимитер"
          onClick={() => setShowCreateDialog(true)}
          variant="contained"
          color="primary"
        />
      </div>

      <ArrayField source="limiters" label="">
        <Datagrid>
          <TextField source="name" label="Название" />
          <ChipField source="type" label="Тип" />
          <NumberField source="limitValue" label="Лимит" />
          <ChipField source="period" label="Период" />
          <BooleanField source="isHardLimit" label="Жесткий" />
          <BooleanField source="isActive" label="Активен" />
          <Button
            label="Удалить"
            onClick={(e) => {
              e.stopPropagation();
              const limiter = e.currentTarget.closest("tr")?.dataset?.record;
              if (limiter) {
                const limiterData = JSON.parse(limiter);
                handleDeleteLimiter(limiterData.id);
              }
            }}
            variant="outlined"
            color="error"
            size="small"
          />
        </Datagrid>
      </ArrayField>

      <Dialog
        open={showCreateDialog}
        onClose={() => setShowCreateDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Создать новый лимитер</DialogTitle>
        <DialogContent>
          <SimpleForm>
            <SelectInput
              source="type"
              label="Тип лимитера"
              choices={limiterTypes}
              validate={[required()]}
              fullWidth
              value={newLimiter.type}
              onChange={(e) =>
                setNewLimiter({ ...newLimiter, type: e.target.value })
              }
            />
            <TextInput
              source="name"
              label="Название"
              validate={[required()]}
              fullWidth
              value={newLimiter.name}
              onChange={(e) =>
                setNewLimiter({ ...newLimiter, name: e.target.value })
              }
            />
            <TextInput
              source="description"
              label="Описание"
              multiline
              rows={2}
              fullWidth
              value={newLimiter.description}
              onChange={(e) =>
                setNewLimiter({ ...newLimiter, description: e.target.value })
              }
            />
            <NumberInput
              source="limitValue"
              label="Значение лимита"
              validate={[required(), minValue(0)]}
              value={newLimiter.limitValue}
              onChange={(e) =>
                setNewLimiter({ ...newLimiter, limitValue: e.target.value })
              }
            />
            <SelectInput
              source="period"
              label="Период"
              choices={limiterPeriods}
              validate={[required()]}
              value={newLimiter.period}
              onChange={(e) =>
                setNewLimiter({ ...newLimiter, period: e.target.value })
              }
            />
            <BooleanInput
              source="isActive"
              label="Активен"
              value={newLimiter.isActive}
              onChange={(e) =>
                setNewLimiter({ ...newLimiter, isActive: e.target.checked })
              }
            />
            <BooleanInput
              source="isHardLimit"
              label="Жесткий лимит"
              value={newLimiter.isHardLimit}
              onChange={(e) =>
                setNewLimiter({ ...newLimiter, isHardLimit: e.target.checked })
              }
            />
          </SimpleForm>
        </DialogContent>
        <DialogActions>
          <Button label="Отмена" onClick={() => setShowCreateDialog(false)} />
          <Button
            label="Создать"
            onClick={handleCreateLimiter}
            variant="contained"
            color="primary"
          />
        </DialogActions>
      </Dialog>
    </div>
  );
};
